import {
  BalanceTransactionReason,
  BillingPeriod,
  DateMacro,
  EqualityOperator,
  Feedback,
  MembershipPackage,
  PaymentMethod,
  Program,
  ProgramAttendee,
  ProgramEventType,
  RefundPolicy,
  RefundReason,
  RegistrationStatus,
  RelationalOperator,
  SaleDateType,
  SessionCreditReason,
  User,
} from '@citruscamps/citrus-client'
import { DateTime, Interval } from 'luxon'
import React, { Fragment } from 'react'
import { DateRangeType, FilterDateRange, FilterNumberRange } from '../interfaces/filter-range'

export const formatFullName = (
  person?: Partial<Pick<User, 'first_name' | 'last_name'>>,
): string => {
  return [person?.first_name, person?.last_name].filter(Boolean).join(' ').trim()
}

export const formatRelation = (attendee: ProgramAttendee, related: boolean = true): string => {
  let relationship = ''
  let age = ''
  const gender = ''
  switch (attendee.relations[0]?.type) {
    case 'parent':
      if (attendee.gender === 'female') {
        relationship = `${related ? 'Your' : 'Their'} daughter`
      } else if (attendee.gender === 'male') {
        relationship = `${related ? 'Your' : 'Their'} son`
      } else {
        relationship = `${related ? 'Your' : 'Their'} child`
      }
      break
    case 'grandparent':
      if (attendee.gender === 'female') {
        relationship = `${related ? 'Your' : 'Their'} granddaughter`
      } else if (attendee.gender === 'male') {
        relationship = `${related ? 'Your' : 'Their'} grandson`
      } else {
        relationship = `${related ? 'Your' : 'Their'} grandchild`
      }
      break
    case 'guardian':
      relationship = `${related ? 'Your' : 'Their'} dependent`
      break
    case 'spouse':
      relationship = `${related ? 'Your' : 'Their'} spouse`
      break
    case 'partner':
      relationship = `${related ? 'Your' : 'Their'} partner`
      break
    case 'sibling':
      if (attendee.gender === 'female') {
        relationship = `${related ? 'Your' : 'Their'} sister`
      } else if (attendee.gender === 'male') {
        relationship = `${related ? 'Your' : 'Their'} brother`
      } else {
        relationship = `${related ? 'Your' : 'Their'} sibling`
      }
      break
    case 'friend':
      relationship = `${related ? 'Your' : 'Their'} friend`
      break
    case 'social_worker':
      relationship = `${related ? 'Your' : 'Their'} child in care`
      break
    case 'self':
      relationship = related ? 'Yourself' : 'Themself'
      break
    case 'other':
      relationship =
        attendee.relations[0].type[0].toUpperCase() +
        attendee.relations[0].type.slice(1).toLowerCase()
      break
  }
  const birthDateTime = DateTime.fromFormat(attendee.date_of_birth || '', 'yyyy-MM-dd')
  if (birthDateTime.isValid) {
    if (attendee.relations[0]?.type !== 'social_worker') {
      age = formatAge(attendee)
    } else {
      age = `Date of Birth: ${attendee.date_of_birth}`
    }
  }
  return [relationship, age, gender].filter(Boolean).join(', ').trim()
}

export const formatAge = (attendee: Partial<ProgramAttendee>): string => {
  if (
    !attendee.date_of_birth ||
    !!DateTime.fromFormat(attendee.date_of_birth, 'yyyy-MM-dd').invalidReason
  )
    return ''
  const birthDateTime = DateTime.fromFormat(attendee.date_of_birth, 'yyyy-MM-dd')
  const yearsOld: number = Interval.fromDateTimes(birthDateTime, DateTime.local()).toDuration(
    'years',
  ).years
  const monthsOld: number = Interval.fromDateTimes(birthDateTime, DateTime.local()).toDuration(
    'months',
  ).months
  return yearsOld >= 1
    ? `${Math.floor(yearsOld)} year${pluralize(Math.floor(yearsOld))} old`
    : `${Math.floor(monthsOld)} month${pluralize(Math.floor(monthsOld))} old`
}

export const formatGender = (attendee: Pick<ProgramAttendee, 'gender'>): string => {
  switch (attendee.gender) {
    case 'female':
      return 'Female'
    case 'male':
      return 'Male'
    case 'other':
      return 'Other'
    case 'not_specified':
    default:
      return 'Not specified'
  }
}

export const formatRating = (value: Partial<Feedback> | { rating: 'none' }): string => {
  switch (value.rating) {
    case 'great':
      return 'Great'
    case 'good':
      return 'Good'
    case 'okay':
      return 'Okay'
    case 'bad':
      return 'Bad'
    case 'terrible':
      return 'Terrible'
    default:
      return 'No Feedback'
  }
}

export const creditReasonLabel = (reason: SessionCreditReason, hasMembership?: boolean) => {
  switch (reason) {
    case 'purchased':
      return `Purchased${hasMembership ? ' with membership' : ''}`
    case 'service_trial':
      return 'Service trial'
    case 'service_unsatisfactory':
      return 'Service unsatisfactory'
    case 'transfer_existing_credit':
      return 'Transferred existing credits'
    case 'manual_registration':
      return 'Manual registration'
    default:
      return 'Other'
  }
}

export const formatRefundReason = (value: string | RefundReason, isPast?: boolean): string => {
  if (isPast) {
    switch (value) {
      case 'requested_by_customer':
        return 'customer request'
      case 'duplicate':
        return 'duplicate order'
      case 'fraudulent':
        return 'fraudulent charge'
      default:
        return 'another reason'
    }
  } else {
    switch (value) {
      case 'requested_by_customer':
        return 'Requested by customer'
      case 'duplicate':
        return 'Duplicate'
      case 'fraudulent':
        return 'Fraudulent'
      default:
        return value.charAt(0) + value.slice(1).toLowerCase().split('_').join(' ')
    }
  }
}

export const formatRefundPolicy = (policy: RefundPolicy, type: ProgramEventType): string => {
  switch (type === 'session' ? policy.session_cancellation_policy : policy.camp_refund_policy) {
    case 'one_day_policy':
      return `${type === 'camp' ? 'Refunds' : 'Cancellations'} up to 24 hours before event`
    case 'three_day_policy':
      return `${type === 'camp' ? 'Refunds' : 'Cancellations'} up to 72 hours before event`
    case 'seven_day_policy':
      return `${type === 'camp' ? 'Refunds' : 'Cancellations'} up to 7 days before event`
    case 'thirty_day_policy':
      return `${type === 'camp' ? 'Refunds' : 'Cancellations'} up to 30 days before event`
    case 'no_refunds':
      return `No ${type === 'camp' ? 'Refunds' : 'Cancellations'}`
    case 'not_defined':
    default:
      return `Contact the organizer to request a ${type === 'camp' ? 'refund' : 'cancellation'}.`
  }
}

export const formatPhoneNumber = (value?: string) => {
  if (!value) return value
  if (typeof value !== 'string') value = `${value}`
  try {
    if (value) {
      let p: RegExpMatchArray | any[] =
        value.replace(/\D/g, '').match(/(\d{1})(\d{3})(\d{3})(\d{4})/) || []
      if (p.length === 5) {
        return `${p[1]} (${p[2]}) ${p[3]}-${p[4]}`
      }
      p = value.replace(/\D/g, '').match(/(\d{3})(\d{3})(\d{4})/) || []
      if (p.length === 4) {
        return `1 (${p[1]}) ${p[2]}-${p[3]}`
      }
      return `+${value}`
    }
  } catch {
    return `+${value}`
  }
  return `+${value}`
}

export const formatAttendeeLabel = (attendee: Partial<ProgramAttendee>) => {
  const gender: string = formatGender(attendee)
  const age: string = formatAge(attendee)
  return [gender, age]
    .filter((i) => !!i)
    .join(', ')
    .trim()
}

export const formatShortBillingPeriod = (billing_period: BillingPeriod): string => {
  switch (billing_period) {
    case 'month':
      return 'mo'
    case 'quarter':
      return 'qr'
    case 'year':
      return 'yr'
    default:
      return 'period'
  }
}

export const pluralize = (value?: number): string => {
  if (value !== 1) {
    return 's'
  } else return ''
}

export const formatDefaultLabel = (value: string): string => {
  if (!value) return ''
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase().split('_').join(' ')
}

export const formatCheckoutMethod = (
  value: string | PaymentMethod,
  language: string = navigator.language,
): string => {
  if (!value) return value
  switch (value) {
    case 'citrus':
      return 'Online payment'
    case 'cash':
      return 'Cash'
    case 'check':
      switch (language) {
        case 'en-CA':
          return 'Cheque'
        default:
          return 'Check'
      }
    case 'invoice':
      return 'Invoice'
    case 'transfer':
      return 'Online transfer'
    case 'account_balance':
      return 'Account balance'
    default:
      return formatDefaultLabel(value)
  }
}

export const formatRegistrationStatus = (value: string | RegistrationStatus): string => {
  switch (value) {
    case 'started':
      return 'Pending payment'
    case 'pending':
      return 'Reserved'
    case 'placed':
      return 'Paid'
    case 'abandoned':
      return 'Abandoned'
    case 'canceled':
      return 'Canceled'
    default:
      return formatDefaultLabel(value)
  }
}

export const formatBalanceReason = (value: string | BalanceTransactionReason): string => {
  switch (value) {
    default:
      return formatDefaultLabel(value)
  }
}

export const formatSalesDate = (value: SaleDateType) => {
  switch (value) {
    case 'one_day_prior':
      return '1 day prior to event'
    case 'three_days_prior':
      return '3 days prior to event'
    case 'one_week_prior':
      return '1 week prior to event'
    case 'one_month_prior':
      return '1 month prior to event'
    default:
      return formatDefaultLabel(value)
  }
}

export const formatScheduledFilterLabel = (
  value?: FilterDateRange,
  scheduleType?: DateRangeType,
) => {
  const isEqual =
    value?.gte &&
    value?.lte &&
    DateTime.fromJSDate(new Date(value.gte)).endOf('day').toISODate() ===
      DateTime.fromJSDate(new Date(value.lte)).toISODate()
  return scheduleType === 'is upcoming' ||
    (value?.gt &&
      DateTime.local().hasSame(DateTime.fromJSDate(new Date(value?.gt)), 'day') &&
      !value?.lt)
    ? 'Upcoming Events'
    : value?.gt && !value?.lt
    ? `All Events after ${DateTime.fromJSDate(new Date(value?.gt)).toLocaleString({
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })}`
    : scheduleType === 'is past' ||
      (value?.lt &&
        DateTime.local().hasSame(DateTime.fromJSDate(new Date(value?.lt)), 'day') &&
        !value?.gt)
    ? 'Past Events'
    : value?.lt && !value?.gt
    ? `All Events before ${DateTime.fromJSDate(new Date(value?.lt)).toLocaleString({
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })}`
    : value?.lte && value?.gte && isEqual
    ? `All Events on ${DateTime.fromJSDate(new Date(value?.gte)).toLocaleString({
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })}`
    : value?.lte && value?.gte
    ? `All Events between ${DateTime.fromJSDate(new Date(value?.gte)).toLocaleString({
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })} - ${DateTime.fromJSDate(new Date(value?.lte)).toLocaleString({
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })}`
    : 'All Events'
}

export const formatCapacityFilterLabel = (value?: FilterNumberRange) => {
  return value?.gt && !value?.lt
    ? `Total registrations greater than ${value?.gt}`
    : value?.lt && !value?.gt
    ? `Total registrations less than ${value?.lt}`
    : value?.lte && value?.gte
    ? `Total registrations equals to ${value?.gte}`
    : value?.lt && value?.gt
    ? `Total registrations between ${value?.gt} - ${value?.lt}`
    : undefined
}

export const formatAppointmentType = (program: Program) => {
  let type = ''
  if (program.types.includes('sports')) {
    type = 'Training'
  } else if (program.types.includes('education')) {
    type = 'Instruction'
  } else {
    type = 'Appointment'
  }
  return `Individual ${type}`
}

export const formatListJSX = (
  values: React.JSX.Element[],
  type: 'and' | 'or' = 'and',
  key: string = 'item',
): React.JSX.Element => {
  if (values.length <= 1) {
    return values[0] || ''
  }
  return (
    <>
      {values.reduce((accu, elem, index) => {
        if (values.length > 2 && index < values.length - 1) {
          return accu.length === 0
            ? [<Fragment key={[key, index].join(':')}>{elem}</Fragment>]
            : [
                ...accu,
                <Fragment key={[key, 'sp', index].join(':')}>, </Fragment>,
                <Fragment key={[key, index].join(':')}>{elem}</Fragment>,
              ]
        }
        if (values.length > 2 && index === values.length - 1) {
          return accu.length === 0
            ? [<Fragment key={[key, index].join(':')}>{elem}</Fragment>]
            : [
                ...accu,
                <Fragment key={[key, 'sp', index].join(':')}>, {type} </Fragment>,
                <Fragment key={[key, index].join(':')}>{elem}</Fragment>,
              ]
        }
        return accu.length === 0
          ? [<Fragment key={[key, index].join(':')}>{elem}</Fragment>]
          : [
              ...accu,
              <Fragment key={[key, 'sp', index].join(':')}> {type} </Fragment>,
              <Fragment key={[key, index].join(':')}>{elem}</Fragment>,
            ]
      }, new Array<React.JSX.Element>())}
    </>
  )
}

export const formatList = (values: string[], type: 'and' | 'or' = 'and') => {
  if (values.length <= 1) {
    return values[0] || ''
  } else if (values.length > 2) {
    return values.slice(0, -1).join(', ').concat(`, ${type} `, values.slice(-1).toString())
  } else {
    return values.slice(0, -1).join(', ').concat(` ${type} `, values.slice(-1).toString())
  }
}

export const formatOperation = (value: EqualityOperator | RelationalOperator): string => {
  switch (value) {
    case 'eq':
      return 'equal to'
    case 'ne':
      return 'not equal to'
    case 'lt':
      return 'less than'
    case 'gt':
      return 'greater than'
    case 'lte':
      return 'less than or equal to'
    case 'gte':
      return 'greater than or equal to'
    default:
      return ''
  }
}

export const formatAgeOperation = (value: RelationalOperator): string => {
  switch (value) {
    case 'lt':
      return 'younger than'
    case 'gt':
      return 'older than'
    case 'lte':
      return 'younger than or is'
    case 'gte':
      return 'older than or is'
    default:
      return ''
  }
}

export const formatDateOperation = (value: RelationalOperator): string => {
  switch (value) {
    case 'lt':
      return 'before'
    case 'gt':
      return 'after'
    case 'lte':
      return 'before or on'
    case 'gte':
      return 'after or on'
    default:
      return ''
  }
}

export const formatAllocatedPeriod = (pack: MembershipPackage) => {
  if (pack.is_unlimited) {
    return 'Unlimited sessions'
  } else if (pack.allocated_amount && pack.allocated_period) {
    return `${pack.allocated_amount} session${pack.allocated_amount === 1 ? '' : 's'} / ${
      pack.allocated_period
    }`
  } else return undefined
}

export const parseDate = (date: string | Date, timezone: string): DateTime =>
  typeof date === 'string'
    ? DateTime.fromJSDate(new Date(date), {
        zone: timezone,
      })
    : DateTime.fromJSDate(date, {
        zone: timezone,
      })

export const formatOrdinals = (num: number): string => {
  const pr = new Intl.PluralRules('en-US', { type: 'ordinal' })
  const suffixes = new Map([
    ['one', 'st'],
    ['two', 'nd'],
    ['few', 'rd'],
    ['other', 'th'],
  ])
  const rule = pr.select(num)
  const suffix = suffixes.get(rule)
  return `${num}${suffix}`
}

export const formatDateMacro = (dateMacro: DateMacro): string => {
  switch (dateMacro) {
    case 'last7days':
      return 'last 7 days'
    case 'last30days':
      return 'last 30 days'
    case 'last3months':
      return 'last 3 months'
    case 'last6months':
      return 'last 6 months'
    case 'thismonth':
      return 'month to date'
    case 'thisquarter':
      return 'quarter to date'
    case 'thisyear':
      return 'year to date'
    case 'lastmonth':
      return 'last month'
    case 'lastquarter':
      return 'last quarter'
    case 'lastyear':
      return 'last year'
    case 'alltime':
      return 'all time'
    case 'custom':
    default:
      return 'Custom'
  }
}

export const formatActivityType = (type: string): string => {
  if (type === 'member' || type === 'invite') {
    type = 'Team ' + type
  } else if (type.includes('program_')) {
    type = type.replace('program_', '')
  }
  return type.charAt(0).toUpperCase() + type.slice(1).split('_').join(' ')
}

export const formatDateRange = ({ start, end }: Interval): string => {
  if (start === null || end === null) {
    return 'Invalid range'
  }
  let range: string = start.toFormat('LLL d')
  if (
    !start.hasSame(end, 'year') &&
    !(start.hasSame(end, 'day') && start.hasSame(DateTime.local(), 'year'))
  ) {
    range += start.toFormat(', yyyy')
  }
  if (!start.hasSame(end, 'day')) {
    range += ` - ${end.toFormat('LLL d')}`
    if (!end.hasSame(DateTime.local(), 'year')) {
      range += end.toFormat(', yyyy')
    }
  }
  return range
}
